<template>
  <section
    id="student-login"
    class="student-login-section"
  >
    <div class="section-container">
      <div class="row align-items-center g-4">
        <!-- Left: Info -->
        <div class="col-12 col-lg-6">
          <span class="section-badge outline">
            Student Portal
          </span>
          <h2 class="section-title mt-2">
            Access Your Student Account
          </h2>
          <p class="section-subtitle">
            Log in with your student ID to book appointments, view your schedule, and manage your OSAS services.
          </p>
          <div class="d-flex flex-column gap-3 mt-4">
            <div class="d-flex align-items-center gap-3">
              <div
                class="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style="width: 40px; height: 40px; background: linear-gradient(135deg, rgba(16,185,129,0.15), rgba(5,150,105,0.15)); color: #059669;"
              >
                <i class="fas fa-calendar-check" />
              </div>
              <div>
                <h6
                  class="fw-bold mb-0"
                  style="color: #1e293b; font-size: 0.9rem;"
                >
                  Book Appointments
                </h6>
                <p
                  class="small mb-0"
                  style="color: #64748b;"
                >
                  Schedule consultations with available time slots
                </p>
              </div>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div
                class="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style="width: 40px; height: 40px; background: linear-gradient(135deg, rgba(59,130,246,0.15), rgba(37,99,235,0.15)); color: #2563eb;"
              >
                <i class="fas fa-history" />
              </div>
              <div>
                <h6
                  class="fw-bold mb-0"
                  style="color: #1e293b; font-size: 0.9rem;"
                >
                  Track Your Appointments
                </h6>
                <p
                  class="small mb-0"
                  style="color: #64748b;"
                >
                  View status and history of your requests
                </p>
              </div>
            </div>
            <div class="d-flex align-items-center gap-3">
              <div
                class="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style="width: 40px; height: 40px; background: linear-gradient(135deg, rgba(139,92,246,0.15), rgba(109,40,217,0.15)); color: #7c3aed;"
              >
                <i class="fas fa-user-cog" />
              </div>
              <div>
                <h6
                  class="fw-bold mb-0"
                  style="color: #1e293b; font-size: 0.9rem;"
                >
                  Manage Your Account
                </h6>
                <p
                  class="small mb-0"
                  style="color: #64748b;"
                >
                  Update your password and view your profile
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Login Form -->
        <div class="col-12 col-lg-6">
          <div class="login-card bg-white rounded-4 p-4 shadow-sm">
            <div class="text-center mb-4">
              <div
                class="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style="width: 60px; height: 60px; background: linear-gradient(135deg, #10b981, #059669); color: #fff;"
              >
                <i
                  class="fas fa-user-graduate"
                  style="font-size: 1.5rem;"
                />
              </div>
              <h5
                class="fw-bold"
                style="color: #1e293b;"
              >
                Student Login
              </h5>
              <p
                class="small"
                style="color: #64748b;"
              >
                Enter your credentials to access the student portal
              </p>
            </div>

            <form
              novalidate
              @submit.prevent="onSubmit"
            >
              <div class="mb-3">
                <label
                  for="loginIdNumber"
                  class="form-label fw-bold small"
                  style="color: #475569;"
                >
                  Student ID Number
                </label>
                <div class="input-group">
                  <span
                    class="input-group-text"
                    style="background: #f8fafc; border-color: #e2e8f0; color: #94a3b8;"
                  >
                    <i class="fas fa-id-card" />
                  </span>
                  <input
                    id="loginIdNumber"
                    v-model="form.id_number"
                    type="text"
                    class="form-control"
                    placeholder="e.g., 2024-00001"
                    :disabled="submitting"
                    style="border-color: #e2e8f0;"
                  >
                </div>
              </div>

              <div class="mb-3">
                <label
                  for="loginPassword"
                  class="form-label fw-bold small"
                  style="color: #475569;"
                >
                  Password
                </label>
                <div class="input-group">
                  <span
                    class="input-group-text"
                    style="background: #f8fafc; border-color: #e2e8f0; color: #94a3b8;"
                  >
                    <i class="fas fa-lock" />
                  </span>
                  <input
                    id="loginPassword"
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    placeholder="Enter your password"
                    :disabled="submitting"
                    style="border-color: #e2e8f0;"
                  >
                  <button
                    type="button"
                    class="input-group-text"
                    style="background: #f8fafc; border-color: #e2e8f0; color: #94a3b8; cursor: pointer;"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="['fas', showPassword ? 'fa-eye' : 'fa-eye-slash']" />
                  </button>
                </div>
              </div>

              <div
                v-if="errorMessage"
                class="alert alert-danger py-2 small"
              >
                <i class="fas fa-exclamation-circle me-1" />
                {{ errorMessage }}
              </div>

              <button
                type="submit"
                class="btn w-100 py-2 fw-bold"
                :disabled="submitting || !form.id_number || !form.password"
                style="background: linear-gradient(135deg, #10b981, #059669); color: white; border: none; border-radius: 10px;"
              >
                <i :class="['fas me-1', submitting ? 'fa-spinner fa-spin' : 'fa-sign-in-alt']" />
                {{ submitting ? 'Signing in...' : 'Sign In' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import '@/assets/css/landing/sections/common.css'
import { SStudentAuth } from '@/services/student'

export default {
  name: 'StudentLoginSection',

  data () {
    return {
      form: {
        id_number: '',
        password: ''
      },
      showPassword: false,
      submitting: false,
      errorMessage: ''
    }
  },

  methods: {
    async onSubmit () {
      if (!this.form.id_number || !this.form.password) return

      this.submitting = true
      this.errorMessage = ''

      try {
        const { data } = await SStudentAuth.signIn(this.form)

        if (data.success) {
          // Store token
          this.$store.dispatch('auth/saveToken', data.token)
          this.$store.dispatch('auth/setUserType', 'student')

          // Fetch student profile
          const { data: profilePayload } = await SStudentAuth.getCurrentUser()
          const profile = profilePayload?.data || profilePayload

          if (!profile?.id) {
            this.$store.dispatch('auth/logout')
            this.errorMessage = 'Unable to load your student profile. Please try again.'
            return
          }

          this.$store.dispatch('auth/fetchUserSuccess', {
            id: profile.id,
            email: profile.email,
            role: 'student',
            role_name: 'Student',
            name: `${profile.first_name} ${profile.last_name}`,
            first_name: profile.first_name,
            last_name: profile.last_name,
            middle_name: profile.middle_name,
            extension_name: profile.extension_name,
            id_number: profile.id_number,
            course: profile.course,
            year_level: profile.year_level,
            contact_number: profile.contact_number
          })

          this.$router.push({ name: 'student.dashboard' })
        } else {
          this.errorMessage = data.message || 'Login failed. Please try again.'
        }
      } catch (error) {
        const msg = error?.message?.message || error?.response?.data?.message || 'Invalid credentials. Please try again.'
        this.errorMessage = msg
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.student-login-section {
  padding: clamp(2rem, 5vw, 4rem) 0;
  background: #f8fafc;
}

.login-card {
  border: 1px solid #e2e8f0;
  max-width: 440px;
  margin: 0 auto;
}

.login-card .form-control:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 0.2rem rgba(16, 185, 129, 0.15);
}
</style>
